import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Droplet, Shield, Sparkles, Zap, Heart, RefreshCw, Hourglass, Brain,
  Phone, Mail, MapPin, Menu, X, Leaf, Check, ArrowRight, Syringe, Activity,
  Stethoscope, FlaskConical, Home, Video, Compass, ChevronRight, ZoomIn,
} from "lucide-react";
import {
  CONTACT, IV_THERAPIES, ADD_ONS, SIGNATURE_INFUSIONS, PERFECT_FOR,
  GLP1, BOTOX_AREAS, FILLER_AREAS, OTHER_SERVICES, WHY_CHOOSE,
} from "../data/content";

const IV_ICONS = [Droplet, Shield, Sparkles, Zap, Heart, RefreshCw, Hourglass, Brain];
const OTHER_ICONS = [Stethoscope, Activity, FlaskConical, Home, Video, Compass];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    custom={i}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
  >
    {children}
  </motion.div>
);

const Eyebrow = ({ children, className = "" }) => (
  <span className={`eyebrow text-sage ${className}`}>{children}</span>
);

const BOOKING_URL = "https://www.optimantra.com/optimus/patient/patientaccess/prospects?pid=dS9DNlAxQzNRRjRwWU5WbXpsT2UvZz09";

const BookingBtn = ({ variant = "primary", className = "", testid, children = "Book Now" }) => {
  const base = "inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-wide transition-all duration-300 px-7 py-3.5 text-sm";
  const styles =
    variant === "primary"
      ? "bg-navy text-cream hover:bg-[#2A3C63] hover:-translate-y-0.5"
      : variant === "sage"
      ? "bg-sage text-white hover:bg-[#6B8074] hover:-translate-y-0.5"
      : "border border-navy-soft text-navy hover:bg-navy hover:text-cream";
  return (
    <a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`${base} ${styles} ${className}`}
      aria-label="Book an appointment"
    >
      {children}
      <ArrowRight size={16} />
    </a>
  );
};

const NAV = [
  { label: "IV Therapy", href: "#iv-therapy" },
  { label: "Membership", href: "#membership" },
  { label: "Weight Loss", href: "#weight-loss" },
  { label: "Aesthetics", href: "#aesthetics" },
  { label: "Services", href: "#services" },
  { label: "Contact", href: "#contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-navy-soft" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className="font-serif-display text-2xl sm:text-[1.65rem] tracking-[0.18em] text-navy">VITALITY</span>
          <span className="eyebrow text-sage mt-0.5 text-[0.55rem]">Integrative Health</span>
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-navy/80 hover:text-sage transition-colors duration-200 font-medium whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:block">
          <BookingBtn testid="header-booking-btn" />
        </div>
        <button
          className="lg:hidden text-navy p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-cream/95 backdrop-blur-xl border-t border-navy-soft px-5 py-5 flex flex-col gap-4">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="text-navy/85 font-medium py-1"
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
          <BookingBtn testid="mobile-booking-btn" className="mt-2 w-full" />
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-28 pb-20 sm:pt-32 sm:pb-24 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-cream to-cream-2/60" />
      <div
        className="absolute inset-x-0 bottom-0 -z-10 h-1/2 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 120%, var(--navy) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center flex flex-col items-center">
        <Reveal>
          <img
            src="/vitality-logo.png"
            alt="Vitality Integrative Health"
            className="w-80 sm:w-[26rem] lg:w-[30rem] h-auto mx-auto"
            data-testid="hero-logo"
          />
        </Reveal>
        <Reveal i={1}>
          <Eyebrow className="block mt-4">Health · Beauty · Wellness</Eyebrow>
        </Reveal>
        <Reveal i={2}>
          <h1
            data-testid="hero-headline"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.06] mt-4 tracking-tight"
          >
            Whole Health. Natural Beauty. <span className="italic text-sage">Vibrant Life.</span>
          </h1>
        </Reveal>
        <Reveal i={3}>
          <p
            data-testid="hero-motto"
            className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-navy mt-5 tracking-wide"
          >
            Balance. Restore. Thrive. <span className="italic text-sage">Glow.</span>
          </p>
        </Reveal>
        <Reveal i={4}>
          <p className="text-navy/70 text-base sm:text-lg max-w-xl mx-auto mt-5 leading-relaxed">
            Personalized IV infusions, medical weight loss, and aesthetic care designed to help
            you restore balance, enhance natural beauty, and thrive every day.
          </p>
        </Reveal>
        <Reveal i={5}>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <BookingBtn testid="hero-booking-btn" />
            <a href="#iv-therapy">
              <span className="inline-flex items-center justify-center gap-2 rounded-full font-medium px-7 py-3.5 text-sm border border-navy-soft text-navy hover:bg-navy hover:text-cream transition-all duration-300">
                Explore IV Menu <ChevronRight size={16} />
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal i={2}>
          <div className="relative mt-14 w-full max-w-3xl mx-auto">
            <div className="relative rounded-[2rem] overflow-hidden card-soft aspect-[16/9]">
              <img src="/hero-spa.jpg" alt="Relaxing wellness experience" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/25 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 left-4 sm:left-8 bg-cream rounded-2xl px-5 py-4 card-soft border border-navy-soft flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sage/15 flex items-center justify-center">
                <Leaf size={18} className="text-sage" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-navy leading-tight">Nurse-Guided Care</p>
                <p className="text-xs text-navy/55">Natural · Safe · Effective</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MarqueeStrip() {
  const items = ["Maximum Absorption", "Quality Ingredients", "Nurse-Guided Care", "Personalized For You", "Evidence-Informed"];
  return (
    <div className="bg-navy text-cream/90 py-4 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-center">
        {items.map((t, i) => (
          <span key={t} className="flex items-center gap-3 text-sm tracking-wide">
            <Sparkles size={14} className="text-sage" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub, center = true }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""} mb-12`}>
      <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
      <Reveal i={1}>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-navy mt-3 tracking-tight">{title}</h2>
      </Reveal>
      {sub && <Reveal i={2}><p className="text-navy/65 mt-4 leading-relaxed">{sub}</p></Reveal>}
    </div>
  );
}

function IVTherapy() {
  return (
    <section id="iv-therapy" className="py-24 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="Our Signature Focus · IV Wellness"
          title="IV Wellness Therapy Menu"
          sub="Our IV therapies deliver essential vitamins, minerals, and nutrients directly to your bloodstream for maximum absorption and results."
        />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5" data-testid="iv-menu-list">
            {IV_THERAPIES.map((iv, idx) => {
              const Icon = IV_ICONS[idx];
              return (
                <Reveal key={iv.n} i={idx % 2}>
                  <div
                    data-testid={`iv-card-${iv.n}`}
                    className="group bg-white rounded-2xl border border-navy-soft p-6 card-soft transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-11 h-11 rounded-xl bg-navy/5 flex items-center justify-center group-hover:bg-sage/15 transition-colors">
                        <Icon size={20} className="text-navy group-hover:text-sage transition-colors" />
                      </div>
                      <span className="font-serif-display text-2xl text-navy">{iv.price}</span>
                    </div>
                    <h3 className="font-serif-display text-xl text-navy leading-tight">{iv.name}</h3>
                    <ul className="mt-3 space-y-1.5 flex-1">
                      {iv.items.map((it) => (
                        <li key={it} className="flex items-center gap-2 text-sm text-navy/65">
                          <span className="w-1.5 h-1.5 rounded-full bg-sage shrink-0" /> {it}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 pt-3 border-t border-navy-soft text-xs text-sage font-medium tracking-wide">
                      {iv.tagline}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Add-on boosters */}
          <Reveal i={1}>
            <div className="bg-navy rounded-2xl p-7 text-cream sticky top-24" data-testid="iv-addon-list">
              <Eyebrow className="!text-sage/90">Enhance Your Drip</Eyebrow>
              <h3 className="font-serif-display text-2xl mt-2 mb-5">Add-On Boosters</h3>
              <ul className="space-y-3">
                {ADD_ONS.map((a) => (
                  <li key={a.name + a.dose} className="flex items-center justify-between gap-3 text-sm border-b border-white/10 pb-2.5">
                    <span className="text-cream/85">
                      {a.name} <span className="text-cream/45">({a.dose})</span>
                    </span>
                    <span className="font-medium text-sage shrink-0">{a.price}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-cream/45 mt-4">* Provider approval required.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function SignatureInfusions() {
  return (
    <section className="py-24 sm:py-28 bg-cream-2/50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-center">
        <Reveal className="lg:col-span-2">
          <div className="rounded-[2rem] overflow-hidden card-soft aspect-[4/5]">
            <img src="/iv-therapy.jpg" alt="IV vitamin therapy infusion" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="lg:col-span-3">
          <Eyebrow>Customized Infusions</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-navy mt-3 tracking-tight">Signature IV Infusions</h2>
          <p className="text-navy/65 mt-4 leading-relaxed max-w-xl">
            Customized IV infusions deliver hydration, vitamins, minerals, amino acids, and antioxidants
            directly into the bloodstream for maximum absorption.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-8">
            {SIGNATURE_INFUSIONS.map((s, i) => (
              <Reveal key={s.name} i={i % 2}>
                <div className="flex gap-3">
                  <Droplet size={18} className="text-sage shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-navy text-[0.95rem]">{s.name}</p>
                    <p className="text-sm text-navy/55 leading-snug">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function MembershipProgram() {
  const [zoom, setZoom] = useState(false);
  return (
    <section id="membership" className="py-24 sm:py-28 bg-cream-2/50">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="Join · Thrive · Glow"
          title="Membership Program"
          sub="Wellness that works. Benefits that last. Monthly memberships designed to help you look, feel & live your best — with exclusive perks, priority access & ongoing savings."
        />

        <Reveal>
          <figure className="bg-white rounded-[1.75rem] border border-navy-soft card-soft p-3 sm:p-5 max-w-2xl mx-auto">
            <button
              type="button"
              onClick={() => setZoom(true)}
              data-testid="membership-flyer-zoom"
              className="group relative block w-full overflow-hidden rounded-2xl cursor-zoom-in"
              aria-label="Enlarge membership program flyer"
            >
              <img
                src="/membership-flyer.jpeg"
                alt="Vitality Integrative Health Membership Program — Essential $119/mo, Thrive $159/mo, Premium $219/mo, Elite $399/mo, plus punch card, VIP, referral, anniversary rewards and package discounts"
                className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                data-testid="membership-flyer"
              />
              <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-navy/85 text-cream text-xs px-3 py-1.5 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn size={13} /> Tap to enlarge
              </span>
            </button>
            <figcaption className="text-center text-xs text-navy/50 mt-3">
              Tap the flyer to view full size · All memberships are billed monthly
            </figcaption>
          </figure>
        </Reveal>

        <Reveal i={1}>
          <div className="flex flex-wrap justify-center gap-4 mt-9">
            <BookingBtn testid="membership-join-btn">Join a Membership</BookingBtn>
            <button
              type="button"
              onClick={() => setZoom(true)}
              className="inline-flex items-center justify-center gap-2 rounded-full font-medium px-7 py-3.5 text-sm border border-navy-soft text-navy hover:bg-navy hover:text-cream transition-all duration-300"
              data-testid="membership-viewfull-btn"
            >
              View Full Flyer <ZoomIn size={16} />
            </button>
          </div>
        </Reveal>
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-[100] bg-navy/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 overflow-auto"
          onClick={() => setZoom(false)}
          data-testid="membership-flyer-modal"
        >
          <button
            type="button"
            onClick={() => setZoom(false)}
            className="fixed top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-cream flex items-center justify-center transition-colors z-[101]"
            aria-label="Close"
            data-testid="membership-flyer-close"
          >
            <X size={22} />
          </button>
          <img
            src="/membership-flyer.jpeg"
            alt="Vitality Integrative Health Membership Program full flyer"
            className="max-w-full sm:max-w-2xl w-auto h-auto rounded-xl shadow-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}

function WeightLoss() {
  const Card = ({ data, accent }) => (
    <div className="bg-white rounded-2xl border border-navy-soft p-7 card-soft h-full flex flex-col">
      <h3 className="font-serif-display text-2xl text-navy leading-tight">{data.name}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-serif-display text-4xl text-navy">{data.membership}</span>
        <span className="text-navy/55 text-sm">/ month membership</span>
      </div>
      <div className={`mt-5 rounded-xl p-1 ${accent}`}>
        <p className="text-center text-xs uppercase tracking-[0.2em] text-white py-2 font-medium">3-Month Bundle Price</p>
      </div>
      <ul className="mt-4 space-y-3 flex-1">
        {data.tiers.map((t) => (
          <li key={t.label} className="flex items-center justify-between border-b border-navy-soft pb-2.5">
            <span className="text-sm text-navy/75">{t.label}</span>
            <span className="font-serif-display text-xl text-navy">{t.price}<span className="text-xs text-navy/45 font-sans">/mo</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <section id="weight-loss" className="py-24 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="Medical Weight Loss · GLP-1 Program"
          title="Nurse Guided. Medically Managed."
          sub="A safe, effective, and personalized approach to help you reach your health and wellness goals — with provider oversight every step of the way."
        />

        <div className="grid lg:grid-cols-3 gap-6" data-testid="weight-loss-pricing">
          <Reveal><Card data={GLP1.tirzepatide} accent="bg-navy" /></Reveal>
          <Reveal i={1}><Card data={GLP1.semaglutide} accent="bg-sage" /></Reveal>
          <Reveal i={2}>
            <div className="bg-navy rounded-2xl p-7 text-cream h-full flex flex-col">
              <Eyebrow className="!text-sage/90">Every Program Includes</Eyebrow>
              <ul className="mt-4 space-y-3">
                {GLP1.includes.map((inc) => (
                  <li key={inc} className="flex gap-3 text-sm text-cream/85">
                    <Check size={17} className="text-sage shrink-0 mt-0.5" /> {inc}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
                <p className="text-xs uppercase tracking-[0.2em] text-cream/55">Additional Consults</p>
                {GLP1.consults.map((c) => (
                  <div key={c.label}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-cream">{c.label}</span>
                      <span className="font-serif-display text-xl text-sage">{c.price}</span>
                    </div>
                    <p className="text-xs text-cream/50 mt-0.5 leading-snug">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Aesthetics() {
  const Block = ({ title, desc, areas, testid }) => (
    <div className="bg-white rounded-2xl border border-navy-soft p-7 card-soft" data-testid={testid}>
      <h3 className="font-serif-display text-2xl text-navy">{title}</h3>
      <p className="text-sm text-navy/60 mt-2 leading-relaxed">{desc}</p>
      <div className="flex flex-wrap gap-2 mt-5">
        {areas.map((a) => (
          <span key={a} className="text-xs px-3 py-1.5 rounded-full bg-navy/5 text-navy/75 border border-navy-soft">{a}</span>
        ))}
      </div>
    </div>
  );
  return (
    <section id="aesthetics" className="py-24 sm:py-28 bg-cream-2/50" data-testid="aesthetics-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-3 order-2 lg:order-1">
          <Eyebrow>Aesthetics · Botox & Dermal Fillers</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-navy mt-3 tracking-tight">Refresh. Enhance. Look Naturally You.</h2>
          <p className="text-navy/65 mt-4 leading-relaxed max-w-xl">
            Wellness-focused aesthetic treatments that smooth fine lines, restore volume, and enhance
            your natural beauty — with subtle, natural-looking results.
          </p>
          <div className="grid sm:grid-cols-2 gap-5 mt-8">
            <Reveal><Block testid="botox-block" title="Botox® Cosmetic" desc="Smooth fine lines and wrinkles for a refreshed, youthful appearance." areas={BOTOX_AREAS} /></Reveal>
            <Reveal i={1}><Block testid="filler-block" title="Dermal Fillers" desc="Restore volume, enhance contours, and achieve natural-looking results." areas={FILLER_AREAS} /></Reveal>
          </div>
        </div>
        <Reveal className="lg:col-span-2 order-1 lg:order-2">
          <div className="rounded-[2rem] overflow-hidden card-soft aspect-[4/5]">
            <img src="/aesthetics.jpg" alt="Aesthetic treatment" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PerfectFor() {
  return (
    <section className="py-24 sm:py-28 bg-navy text-cream relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #fff 0%, transparent 45%)" }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Eyebrow className="!text-sage">Is This For You?</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight">Perfect For</h2>
            <p className="text-cream/65 mt-4 max-w-md leading-relaxed">
              Whether you want to restore energy, recover faster, glow from within, or simply feel
              your best — there's a therapy designed for your goals.
            </p>
            <div className="mt-8">
              <BookingBtn variant="sage" testid="perfectfor-booking-btn" />
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4" data-testid="perfect-for-list">
            {PERFECT_FOR.map((p, i) => (
              <Reveal key={p} i={i % 2}>
                <li className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-4">
                  <Check size={18} className="text-sage shrink-0" />
                  <span className="text-cream/90 text-sm">{p}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function OtherServices() {
  return (
    <section id="services" className="py-24 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="Whole-Person Care"
          title="More Ways We Support You"
          sub="Beyond our signature IV and aesthetic services, Vitality Integrative Health offers comprehensive, personalized care for every part of your wellness journey."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="other-services-grid">
          {OTHER_SERVICES.map((s, i) => {
            const Icon = OTHER_ICONS[i];
            return (
              <Reveal key={s.name} i={i % 3}>
                <div className="group bg-white rounded-2xl border border-navy-soft p-6 card-soft transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className="w-11 h-11 rounded-xl bg-sage/12 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-sage" />
                  </div>
                  <h3 className="font-serif-display text-xl text-navy">{s.name}</h3>
                  <p className="text-sm text-navy/60 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="py-24 sm:py-28 bg-cream-2/50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-center">
        <Reveal className="lg:col-span-2">
          <div className="rounded-[2rem] overflow-hidden card-soft aspect-[4/5]">
            <img src="/concierge.jpg" alt="Personalized concierge wellness" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="lg:col-span-3">
          <Eyebrow>The Vitality Difference</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-navy mt-3 tracking-tight">Why Choose Vitality?</h2>
          <ul className="mt-7 space-y-4" data-testid="trust-reasons-list">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w} i={i}>
                <li className="flex items-center gap-4 bg-white rounded-xl border border-navy-soft px-5 py-4 card-soft">
                  <div className="w-8 h-8 rounded-full bg-sage/15 flex items-center justify-center shrink-0">
                    <Check size={16} className="text-sage" />
                  </div>
                  <span className="text-navy/85">{w}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <div className="mt-7 bg-navy/[0.04] border border-navy-soft rounded-xl p-5">
            <p className="text-sm text-navy/65 leading-relaxed">
              <strong className="text-navy font-semibold">Safety & Considerations.</strong> IV therapy is safe
              for most healthy individuals but may not be appropriate for everyone. A consultation is required.
              Please inform us of any medical conditions, medications, or allergies. Your safety is our priority.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-24 sm:py-28 bg-cream">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="relative rounded-[2.5rem] overflow-hidden bg-navy px-8 sm:px-16 py-16 sm:py-20 text-center">
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 0%, transparent 40%), radial-gradient(circle at 85% 75%, var(--sage) 0%, transparent 45%)" }} />
            <div className="relative">
              <Eyebrow className="!text-sage">Schedule Your Appointment Today</Eyebrow>
              <h2 className="font-serif-display text-3xl sm:text-5xl text-cream mt-4 tracking-tight leading-tight">
                Invest in your health.<br />Elevate your life.
              </h2>
              <p className="text-cream/65 mt-5 max-w-lg mx-auto">
                You deserve to feel your best — we're here to help you get there.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-9">
                <BookingBtn variant="sage" testid="cta-booking-btn" />
                <a href={CONTACT.phoneHref}>
                  <span className="inline-flex items-center justify-center gap-2 rounded-full font-medium px-7 py-3.5 text-sm border border-cream/25 text-cream hover:bg-cream hover:text-navy transition-all duration-300">
                    <Phone size={16} /> {CONTACT.phone}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer id="contact" className="bg-navy-deep text-cream pt-16 pb-10" style={{ backgroundColor: "var(--navy-deep)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-3 gap-10 pb-10 border-b border-white/10">
          <div>
            <span className="font-serif-display text-2xl tracking-[0.18em] text-cream">VITALITY</span>
            <p className="eyebrow text-sage mt-1 text-[0.55rem]">Integrative Health</p>
            <p className="text-cream/55 text-sm mt-5 leading-relaxed max-w-xs">
              Whole Health. Natural Beauty. Vibrant Life. Balance · Restore · Thrive · Glow.
            </p>
          </div>
          <div>
            <p className="eyebrow text-cream/45 mb-4">Get In Touch</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-sage shrink-0" />
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-contact-email" className="text-cream/80 hover:text-sage break-all transition-colors">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-sage shrink-0" />
                <a href={CONTACT.phoneHref} data-testid="footer-contact-phone" className="text-cream/80 hover:text-sage transition-colors">
                  {CONTACT.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={16} className="text-sage shrink-0" />
                <span className="text-cream/80">{CONTACT.location}</span>
              </li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-cream/45 mb-4">Now Traveling To</p>
            <div className="flex flex-wrap gap-2">
              {CONTACT.nearby.map((c) => (
                <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-cream/75">{c}</span>
              ))}
            </div>
            <div className="mt-6">
              <BookingBtn variant="sage" testid="footer-booking-btn" />
            </div>
          </div>
        </div>
        <div className="pt-7 flex flex-col sm:flex-row items-center justify-between gap-3 text-center">
          <p className="text-xs text-cream/40">
            © {new Date().getFullYear()} Vitality Integrative Health. All rights reserved.
          </p>
          <p className="text-xs text-cream/40 tracking-[0.15em] uppercase">
            Whole Health · Natural Beauty · Vibrant Life
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-cream overflow-x-hidden">
      <Header />
      <Hero />
      <MarqueeStrip />
      <IVTherapy />
      <SignatureInfusions />
      <MembershipProgram />
      <WeightLoss />
      <Aesthetics />
      <PerfectFor />
      <OtherServices />
      <WhyChoose />
      <CTASection />
      <Footer />
    </div>
  );
}
